import { useWorkspaceText } from '../interface-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useNamespaceCopy } from '@/i18n/ns';
import { overlaysCopy } from '@/i18n/ns/overlays';
import { DefinitionFields, DefinitionPreview, definitionValues } from './definition-fields';
import { ScheduleFields, Choice } from './setup-fields';
import { describeSchedule, formSchedule, scheduleForm } from './schedule';
import type { AgentSchedule } from './api';
import './agents.css';

// The live chat proposal and its direct edits use the same server-staged
// arguments. Editing never authorizes creation or schedule activation.
export function AgentProposal({
  args,
  tab,
  editing,
  onChange,
  triggerOnly = false,
}: {
  args: Record<string, unknown>;
  tab: string;
  editing: boolean;
  onChange: (next: Record<string, unknown>) => void;
  triggerOnly?: boolean;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const scheduleCopy = useNamespaceCopy(overlaysCopy);
  const locale = useChatInterfaceLocale();
  const set = (patch: Record<string, unknown>) => onChange({ ...args, ...patch });
  const schedule = args.schedule as AgentSchedule | null;
  // A draft (New agent) is saved unpublished with its schedule off: approving it runs nothing.
  const draft = args.draft === true;
  if (tab === 'schedule' || triggerOnly)
    return (
      <div className="ws-agents-form">
        <div className="ws-agent-definition-section">
          <h4>{uiText('Run on demand')}</h4>
          <p>
            {uiText(
              'Run is always available. A schedule is optional and lives separately from the agent’s instructions.',
            )}
          </p>
        </div>
        {editing ? (
          <>
            <ScheduleFields
              value={scheduleForm(schedule ?? null)}
              disabled={false}
              onChange={(value) =>
                set({
                  schedule: formSchedule(value),
                  ...(!formSchedule(value) ? { enabled: false } : {}),
                })
              }
            />
            {schedule && !draft && (
              <label className="ws-agents-switch">
                <input
                  type="checkbox"
                  checked={args.enabled === true}
                  onChange={(event) => set({ enabled: event.target.checked })}
                />
                <span>{uiText('Enable this schedule when approved')}</span>
              </label>
            )}
          </>
        ) : (
          <div className="ws-agent-definition-section">
            <h4>{uiText('Schedule')}</h4>
            <p>{describeSchedule(schedule ?? null, scheduleCopy, locale)}</p>
            {schedule?.timezone && <small>{schedule.timezone}</small>}
            <small>
              {schedule
                ? draft
                  ? uiText('Kept off until you create the agent from its canvas.')
                  : args.enabled
                    ? uiText('Will be enabled when you approve.')
                    : uiText('Will be saved paused. You can enable it later.')
                : uiText('Only runs when you ask.')}
            </small>
          </div>
        )}
        <p className="ws-agents-note">
          {draft
            ? uiText(
                'Draft · approving saves it, and nothing runs. Test it and create it from its canvas.',
              )
            : uiText(
                'Draft · nothing is scheduled until you approve. Connected actions require their own approval at run time.',
              )}
        </p>
      </div>
    );
  const value = definitionValues(args);
  return (
    <div className="ws-agents-form">
      {editing ? (
        <>
          {'name' in args && (
            <label className="ws-agents-field">
              <span>{uiText('Name')}</span>
              <input
                aria-label={uiText('Agent name')}
                value={String(args.name ?? '')}
                maxLength={120}
                onChange={(event) => set({ name: event.target.value })}
              />
            </label>
          )}
          <DefinitionFields value={value} onChange={set} />
          {'scope' in args && (
            <Choice
              disabled={false}
              label={uiText('Who can use it')}
              value={String(args.scope ?? 'personal')}
              options={[
                { value: 'personal', label: uiText('Only me') },
                { value: 'company', label: uiText('Company') },
              ]}
              onChange={(scope) => set({ scope })}
            />
          )}
        </>
      ) : (
        <>
          <DefinitionPreview value={value} />
          {'scope' in args && (
            <p className="ws-muted">
              {args.scope === 'company'
                ? uiText('Company agent · company resources only')
                : uiText('Personal agent · runs with your access')}
            </p>
          )}
          {draft && (
            <p className="ws-agents-note">
              {uiText('Approving saves it as a draft. It does not run automatically.')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
